/* Copyright (c) 2020-present CloudIO Inc */

import { atom, useStateXAtomValue } from '@cloudio-saas/statex';
import { Unstable_Grid2 as Grid, Theme } from '@mui/material';
import { constants, DomProps, GridFlex } from 'componentregistry';
import { makeStyles } from '@mui/styles';
import React, { ReactNode } from 'react';
import clsx from 'clsx';

const { fieldVariant, notificationLocation } = constants;

export const fieldVariantAtom = atom<keyof typeof fieldVariant>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'fieldVariant'],
  defaultValue: 'standard',
});

export const notificationLocationAtom = atom<keyof typeof notificationLocation>(
  {
    path: ['db', 'IoAppSettings', ':appUid', 'md', 'notificationLocation'],
    defaultValue: 'bottom-right',
  },
);

export function useFieldVariant(appUid: string) {
  return useStateXAtomValue(fieldVariantAtom, {
    params: { appUid },
  });
}

export const fullscreenToggleAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'fullscreenToggle'],
  defaultValue: true,
});
export const headerNavigationAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'headerNavigation'],
  defaultValue: true,
});
export const darkModeToggleAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'darkModeToggle'],
  defaultValue: true,
});
export const showRoleSwitcherAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'showRoleSwitcher'],
  defaultValue: false,
});
export const showRoleNameAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'showRoleName'],
  defaultValue: true,
});
export const useBaseFieldStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
  },
  dirty: {
    '&::before': {
      borderColor: `${theme.palette.error.main} transparent transparent ${theme.palette.error.main}`,
      borderStyle: 'solid',
      borderWidth: '4px',
      content: '""',
      height: 0,
      left: 0,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 0,
      verticalAlign: 'top',
      width: 0,
      zIndex: 1,
    },
  },
}));

interface Props {
  dirty?: boolean;
  flex: GridFlex;
  className?: string;
  domProps: DomProps;
  children?: ReactNode;
}

export const FieldWrapper = React.forwardRef(
  (props: Props, ref: React.Ref<HTMLDivElement>): React.ReactElement => {
    const { children, className, dirty, domProps, flex } = props;
    const classes = useBaseFieldStyles();

    return (
      <Grid
        className={clsx(
          classes.root,
          {
            [classes.dirty]: dirty,
            dirty: dirty,
          },
          className,
        )}
        lg={flex?.all ?? flex?.lg ?? false}
        md={flex?.all ?? flex?.md ?? false}
        sm={flex?.all ?? flex?.sm ?? false}
        xl={flex?.all ?? flex?.xl ?? false}
        xs={flex?.all ?? flex?.xs ?? false}
        ref={ref}
        {...domProps}
      >
        {children}
      </Grid>
    );
  },
);
